/**
 * Port Types
 * ===========
 * Defines the data types that can flow between nodes.
 * 
 * Each port type specifies:
 * - id: Unique identifier
 * - label: Display name
 * - color: Visual color for connections (CSS variable or hex)
 * - accepts: Array of compatible types (for input ports)
 * - validate: Optional validation function
 * 
 * DESIGN PRINCIPLE:
 * Keep types simple and broad. More specific validation
 * happens at the node level, not the port level.
 */

/**
 * Port type identifiers
 */
export const PortType = Object.freeze({
  IMAGE: 'image',
  MASK: 'mask',
  VIDEO: 'video',
  AUDIO: 'audio',
  MODEL_3D: '3d',
  PERFORMANCE: 'performance',
  TEXT: 'text',
  NUMBER: 'number',
  BOOLEAN: 'boolean',
  COLOR: 'color',
  IMAGE_ARRAY: 'image-array',
  ASSET: 'asset',      // Any asset (image, video, audio, 3D)
  ELEMENT: 'element',  // Character/environment/object/style element
  ANY: 'any',          // Wildcard - accepts anything
  TRIGGER: 'trigger',  // Execution trigger (no data, just flow control)
  STORY: 'story'       // Non-executable media sequencing relationship
});

/**
 * Semantic icon identifiers for port chrome.
 *
 * Keep these separate from PortType so an icon can be reused deliberately
 * without making the renderer guess from a type string.
 */
export const PortIcon = Object.freeze({
  IMAGE: 'image',
  MASK: 'mask',
  VIDEO: 'video',
  AUDIO: 'audio',
  MODEL_3D: '3d',
  PERFORMANCE: 'performance',
  TEXT: 'text',
  NUMBER: 'number',
  BOOLEAN: 'boolean',
  COLOR: 'color',
  IMAGE_ARRAY: 'image-array',
  ASSET: 'asset',
  ELEMENT: 'element',
  ANY: 'any',
  TRIGGER: 'trigger',
  STORY: 'story',
  UNKNOWN: 'unknown',
});

/**
 * SVG path catalog for port icons. The type definition selects a semantic icon;
 * ThreadPorts only renders these paths and owns no second type-to-glyph map.
 */
export const PORT_ICON_PATHS = Object.freeze({
  [PortIcon.IMAGE]: [
    'M4 3h8a1.5 1.5 0 0 1 1.5 1.5v7A1.5 1.5 0 0 1 12 13H4a1.5 1.5 0 0 1-1.5-1.5v-7A1.5 1.5 0 0 1 4 3Z',
    'M6 5.25a1 1 0 1 0 0 2 1 1 0 0 0 0-2Z',
    'm3.5 11 3.25-3.25 2.25 2 1.5-1.5 2 2.75',
  ],
  [PortIcon.IMAGE_ARRAY]: [
    'M5.4 3.5h6.2A1.4 1.4 0 0 1 13 4.9v5.2a1.4 1.4 0 0 1-1.4 1.4H5.4A1.4 1.4 0 0 1 4 10.1V4.9a1.4 1.4 0 0 1 1.4-1.4Z',
    'M2.5 5.5v6.25c0 .7.55 1.25 1.25 1.25h7',
    'M5.25 10 7.5 7.8l1.4 1.25L10 7.95 11.75 10',
  ],
  [PortIcon.MASK]: [
    'M8 2.75a5.25 5.25 0 1 0 0 10.5 5.25 5.25 0 0 0 0-10.5Z',
    'M8 2.75v10.5',
    'M8 5.25a2.75 2.75 0 0 1 0 5.5',
  ],
  [PortIcon.VIDEO]: [
    'M4 3.5h5.5A1.5 1.5 0 0 1 11 5v6a1.5 1.5 0 0 1-1.5 1.5H4A1.5 1.5 0 0 1 2.5 11V5A1.5 1.5 0 0 1 4 3.5Z',
    'm11 6 2.5-1.5v7L11 10',
    'm6 6 2.75 2L6 10Z',
  ],
  [PortIcon.AUDIO]: ['M3 9.5v-3', 'M5.5 12V4', 'M8 10.5v-5', 'M10.5 12.5v-9', 'M13 9V7'],
  [PortIcon.MODEL_3D]: [
    'M8 2.5 13 5.25v5.5L8 13.5l-5-2.75v-5.5Z',
    'M3.25 5.5 8 8.25l4.75-2.75',
    'M8 8.25V13.5',
  ],
  // A figure mid-motion: this port carries a solved body performance, not renderable media.
  [PortIcon.PERFORMANCE]: [
    'M8 2.75a1.25 1.25 0 1 0 0 2.5 1.25 1.25 0 0 0 0-2.5Z',
    'M8 5.5v4',
    'M4.75 7.25 8 6.25l3.25 1',
    'm8 9.5-2.25 3.75',
    'm8 9.5 2.25 3.75',
  ],
  [PortIcon.TEXT]: ['M4 4h8', 'M8 4v8', 'M6.25 12h3.5'],
  [PortIcon.NUMBER]: ['M6 3.5 5 12.5', 'M11 3.5 10 12.5', 'M3.75 6.25h9', 'M3.25 9.75h9'],
  [PortIcon.BOOLEAN]: ['m4 8.25 2.5 2.5L12 5.25'],
  [PortIcon.COLOR]: [
    'M8 3a5 5 0 1 0 0 10A5 5 0 0 0 8 3Z',
    'M8 3v10',
    'M3 8h10',
  ],
  [PortIcon.ASSET]: [
    'M3 5.5 8 3l5 2.5v5L8 13l-5-2.5Z',
    'M3.25 5.75 8 8.25l4.75-2.5',
    'M8 8.25V13',
  ],
  [PortIcon.ELEMENT]: ['M8 2.75 9.2 6.6 13 8l-3.8 1.4L8 13.25 6.8 9.4 3 8l3.8-1.4Z'],
  [PortIcon.ANY]: ['M8 2.75v10.5', 'M2.75 8h10.5', 'M4.25 4.25l7.5 7.5', 'M11.75 4.25l-7.5 7.5'],
  [PortIcon.TRIGGER]: ['M5.5 3.75 12 8l-6.5 4.25Z'],
  [PortIcon.STORY]: [
    'M4 4.5h2.5',
    'M9.5 4.5H12',
    'M4 11.5h2.5',
    'M9.5 11.5H12',
    'M6.75 4.5c2.25 0 2.25 7 4.5 7',
  ],
  [PortIcon.UNKNOWN]: [
    'M8 3a5 5 0 1 0 0 10A5 5 0 0 0 8 3Z',
    'M8 5.25v.1',
    'M8 7.5v3.25',
  ],
});

/**
 * Port type definitions with metadata
 */
export const PORT_TYPE_DEFINITIONS = {
  [PortType.IMAGE]: {
    id: PortType.IMAGE,
    label: 'Image',
    color: 'var(--port-image, #4CAF50)',
    icon: PortIcon.IMAGE,
    accepts: [PortType.IMAGE, PortType.ASSET],
    description: 'Raster image data (PNG, JPG, WebP)'
  },
  
  [PortType.MASK]: {
    id: PortType.MASK,
    label: 'Mask',
    color: 'var(--port-mask, #9C27B0)',
    icon: PortIcon.MASK,
    // Deliberately NOT ASSET. A mask has an image media carrier, but remains a distinct
    // semantic port type. An unresolved ASSET could carry video, so it must not feed a
    // mask input merely because its concrete media type is not yet known.
    accepts: [PortType.MASK, PortType.IMAGE],
    description: 'Binary or grayscale mask for selections'
  },

  [PortType.VIDEO]: {
    id: PortType.VIDEO,
    label: 'Video',
    color: 'var(--port-video, var(--accent-secondary))',
    icon: PortIcon.VIDEO,
    accepts: [PortType.VIDEO, PortType.ASSET],
    description: 'Video media asset (MP4, WebM, MOV)'
  },

  [PortType.AUDIO]: {
    id: PortType.AUDIO,
    label: 'Audio',
    color: 'var(--port-audio, var(--accent-primary))',
    icon: PortIcon.AUDIO,
    accepts: [PortType.AUDIO, PortType.ASSET],
    description: 'Audio media asset (MP3, WAV, OGG)'
  },

  [PortType.MODEL_3D]: {
    id: PortType.MODEL_3D,
    label: '3D Model',
    color: 'var(--port-3d, var(--accent-tertiary))',
    icon: PortIcon.MODEL_3D,
    accepts: [PortType.MODEL_3D, PortType.ASSET],
    description: '3D model asset (GLB, GLTF, OBJ)'
  },

  [PortType.PERFORMANCE]: {
    id: PortType.PERFORMANCE,
    label: 'Performance',
    color: 'var(--port-performance, var(--accent-tertiary))',
    icon: PortIcon.PERFORMANCE,
    // Deliberately NOT ASSET, for the same reason MASK is not (see above). A performance's
    // source bytes are a clip JSON, not renderable media; an ASSET-accepting port would let
    // it reach an image or video executor, which would decode nothing and bill a provider
    // for the attempt. It accepts only itself.
    accepts: [PortType.PERFORMANCE],
    description: 'Solved 3D body performance clip'
  },
  
  [PortType.TEXT]: {
    id: PortType.TEXT,
    label: 'Text',
    color: 'var(--port-text, #2196F3)',
    icon: PortIcon.TEXT,
    accepts: [PortType.TEXT],
    description: 'Text string (prompts, labels, etc.)'
  },
  
  [PortType.NUMBER]: {
    id: PortType.NUMBER,
    label: 'Number',
    color: 'var(--port-number, #FF9800)',
    icon: PortIcon.NUMBER,
    accepts: [PortType.NUMBER],
    description: 'Numeric value (integer or float)'
  },
  
  [PortType.BOOLEAN]: {
    id: PortType.BOOLEAN,
    label: 'Boolean',
    color: 'var(--port-boolean, #F44336)',
    icon: PortIcon.BOOLEAN,
    accepts: [PortType.BOOLEAN],
    description: 'True or false value'
  },
  
  [PortType.COLOR]: {
    id: PortType.COLOR,
    label: 'Color',
    color: 'var(--port-color, #E91E63)',
    icon: PortIcon.COLOR,
    accepts: [PortType.COLOR, PortType.TEXT],
    description: 'Color value (hex, rgb, hsl)'
  },

  [PortType.IMAGE_ARRAY]: {
    id: PortType.IMAGE_ARRAY,
    label: 'Image Array',
    color: 'var(--port-image-array, #2E7D32)',
    icon: PortIcon.IMAGE_ARRAY,
    accepts: [PortType.IMAGE_ARRAY],
    description: 'Ordered list of image assets'
  },
  
  [PortType.ASSET]: {
    id: PortType.ASSET,
    label: 'Asset',
    color: 'var(--port-asset, #00BCD4)',
    icon: PortIcon.ASSET,
    accepts: [
      PortType.ASSET,
      PortType.IMAGE,
      PortType.VIDEO,
      PortType.AUDIO,
      PortType.MODEL_3D,
    ],
    description: 'Any asset type (image, video, audio, 3D)'
  },

  [PortType.ELEMENT]: {
    id: PortType.ELEMENT,
    label: 'Element',
    color: 'var(--port-element, #AB47BC)',
    icon: PortIcon.ELEMENT,
    accepts: [PortType.ELEMENT],
    description: 'Character, environment, object, or style element with reference images'
  },
  
  [PortType.ANY]: {
    id: PortType.ANY,
    label: 'Any',
    color: 'var(--port-any, #607D8B)',
    icon: PortIcon.ANY,
    accepts: Object.values(PortType),
    description: 'Dynamic value that can receive or produce any data type'
  },
  
  [PortType.TRIGGER]: {
    id: PortType.TRIGGER,
    label: 'Trigger',
    color: 'var(--port-trigger, #795548)',
    icon: PortIcon.TRIGGER,
    accepts: [PortType.TRIGGER],
    description: 'Execution flow control (no data)'
  },

  [PortType.STORY]: {
    id: PortType.STORY,
    label: 'Story',
    color: 'var(--port-story, var(--accent-primary))',
    icon: PortIcon.STORY,
    accepts: [PortType.STORY],
    description: 'Canvas story-thread sequencing relationship'
  }
};

/**
 * Check if two port types are compatible for connection
 * 
 * @param {string} outputType - Type of the output port
 * @param {string} inputType - Type of the input port
 * @returns {boolean} True if connection is valid
 */
export function arePortTypesCompatible(outputType, inputType) {
  // ANY accepts everything
  if (inputType === PortType.ANY) {
    return true;
  }

  // ANY outputs are dynamic values from routing/batch utility nodes. Let the
  // receiving node validate the concrete runtime value.
  if (outputType === PortType.ANY) {
    return true;
  }
  
  // Direct match
  if (outputType === inputType) {
    return true;
  }
  
  // Check if input accepts the output type
  const inputDef = PORT_TYPE_DEFINITIONS[inputType];
  if (inputDef && inputDef.accepts) {
    return inputDef.accepts.includes(outputType);
  }
  
  return false;
}

/**
 * Get the display color for a port type
 * 
 * @param {string} portType - Port type identifier
 * @returns {string} CSS color value
 */
export function getPortColor(portType) {
  const def = PORT_TYPE_DEFINITIONS[portType];
  return def?.color || 'var(--text-tertiary)';
}

/**
 * Get port type definition
 * 
 * @param {string} portType - Port type identifier
 * @returns {Object|null} Port type definition
 */
export function getPortTypeDefinition(portType) {
  return PORT_TYPE_DEFINITIONS[portType] || null;
}

/**
 * Resolve the complete visual presentation for a port type from one catalog.
 *
 * @param {string} portType - Port type identifier
 * @returns {{type: string|null, label: string, color: string, icon: string, definition: Object|null}}
 */
export function getPortPresentation(portType) {
  const definition = getPortTypeDefinition(portType);
  return {
    type: definition?.id || null,
    label: definition?.label || 'Port',
    color: definition?.color || 'var(--text-tertiary)',
    icon: definition?.icon || PortIcon.UNKNOWN,
    definition,
  };
}

export default PortType;
